import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { getPlatformAppVersions } from '@services/platformApps.services';
import {
  platformAppsVersionsRequest,
  setPlatformAppsVersions,
} from '@slices/cache/platformApps';
import {
  contextNamesSelector,
  platformAppsTemplateSelector,
  platformAppsVersionsSelector,
} from '@selectors';
import { useDispatch, useSelector } from '@hooks';
import { invariant, toastifyResponseError } from '@utils';

type Params = {
  version: string | undefined;
  appName: string;
};

export const usePlatformAppsTemplates = (params?: Params) => {
  const { version: versionParam = params?.version, appName = params?.appName } =
    useParams();

  invariant(appName);

  const dispatch = useDispatch();
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);
  const template = useSelector(platformAppsTemplateSelector(appName));
  const {
    fetched,
    templates,
    appName: templateName,
  } = useSelector(platformAppsVersionsSelector);

  const isVersion = !!versionParam;
  const isVersionLatest = versionParam === 'latest' || !isVersion;

  const templateVersion = template?.version ?? '';

  const latestVersion = templateVersion;

  const versions = templates.map(({ version }) => version);

  const formattedVersion =
    isVersion && !isVersionLatest
      ? decodeURIComponent(versionParam)
      : latestVersion;

  const versionTemplate = isVersion
    ? templates.find(({ version }) => version === formattedVersion)
    : template;

  useEffect(() => {
    const getVersions = async () => {
      try {
        dispatch(platformAppsVersionsRequest());

        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        const templates = await getPlatformAppVersions({
          organizationName,
          clusterName,
          projectName,
          appName,
        });

        dispatch(setPlatformAppsVersions({ appName, templates }));
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    if (templateName !== appName) {
      getVersions();
    }
  }, [
    organizationName,
    clusterName,
    projectName,
    appName,
    templateName,
    fetched,
    dispatch,
  ]);

  return {
    isFetched: fetched,
    template: versionTemplate,
    versions,
    latestVersion,
    version: formattedVersion,
    isVersion,
    templates,
  };
};
